import {
    alpha,
    ThemeProvider,
    Box,
    Chip,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Select,
    Skeleton,
    Stack,
    Tab,
    Typography,
    useMediaQuery
} from '@mui/material';
import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import TableCard from '../../system/table-card';
import moment from 'moment';
import Comments from '../../views/comments';
import { AppVarsContext } from '../../context/AppVarsContext';
import ResponsiveTabs from '../../system/responsive-tabs';
import ReactApexChart from 'react-apexcharts';
import { createReportSurfaceTheme, getReportSurfaceSx, reportColors, styleReportChart, getReportPageSx, getAnalyticsReportTabsSx } from '../shared/report-design';
import ReportQuestionAssistant from '../../system/controls/report-question-assistant';
import { resolveShowCost } from '../../system/report-visibility';
import { formatUsd } from '../../system/report-formatters';

import ReportPerformanceOverview, { ReportSectionIntro } from '../shared/ReportPerformanceOverview';

const fontSize = '12px';

const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));
const formatPercent = (value) => `${Number(value || 0).toFixed(2)}%`;
const formatCurrency = (value) => formatUsd(value);

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <Box sx={{ minWidth: 0 }} role="tabpanel" id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} hidden={value !== index} {...other}>
            {value === index && <Box sx={{ minWidth: 0 }}>{children}</Box>}
        </Box>
    );
}

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

const pick = (obj, keys) => {
    for (const key of keys) {
        if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
        const hit = Object.keys(obj || {}).find((entry) => entry.toUpperCase() === String(key).toUpperCase());
        if (hit && obj[hit] !== undefined && obj[hit] !== null) return obj[hit];
    }
    return undefined;
};

const normalizeCampaignRows = (rows = []) =>
    rows.map((row) => ({
        ...row,
        CAMPAIGN: pick(row, ['CAMPAIGN', 'Campaign', 'campaign', 'campaign_name', 'CAMPAIGN_NAME']) ?? ''
    }));

const normalizeKeywordRows = (rows = []) =>
    rows.map((row) => ({
        ...row,
        SEARCH: pick(row, ['SEARCH', 'TERM', 'KEYWORD', 'QUERY', 'SEARCH_TERM', 'SEARCHQUERY', 'SEARCH_QUERY', 'search', 'searchQuery']) ?? ''
    }));

const normalizeRegionRows = (rows = []) =>
    rows.map((row) => ({
        ...row,
        CITY: pick(row, ['CITY', 'City', 'city', 'METRO', 'Region']) ?? ''
    }));

const normalizePerformanceRows = (rows = [], labelKeys = []) =>
    rows
        .map((row) => {
            const label = pick(row, labelKeys) || 'Unknown';
            const impressions = safeNum(pick(row, ['IMPRESSIONS', 'Impressions', 'impressions', 'IMPR', 'impr']));
            const clicks = safeNum(pick(row, ['CLICKS', 'Clicks', 'clicks']));
            const conversions = safeNum(pick(row, ['CONVERSIONS', 'Conversions', 'conversions', 'CONV', 'conv']));
            const spend = safeNum(pick(row, ['COST', 'Cost', 'SPEND', 'Spend']));
            const revenue = safeNum(pick(row, ['REVENUE', 'Revenue']));
            const ctr = impressions ? (clicks / impressions) * 100 : safeNum(pick(row, ['CTR', 'ctr']));
            const convRate = clicks ? (conversions / clicks) * 100 : safeNum(pick(row, ['CONV_RATE', 'CONVRATE', 'convrate']));
            return {
                label,
                impressions,
                clicks,
                conversions,
                spend,
                revenue,
                ctr,
                convRate,
                raw: row
            };
        })
        .filter((row) => row.label && (row.impressions > 0 || row.clicks > 0 || row.conversions > 0 || row.spend > 0 || row.revenue > 0));

const buildTableOptions = (theme, rowCount) => ({
    rowStyle: { fontSize, padding: 0, margin: 0 },
    cellStyle: {
        border: `1px solid ${theme.palette.divider}`,
        textAlign: 'center',
        padding: theme.spacing(0.5),
        fontSize
    },
    headerStyle: {
        textAlign: 'center',
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.secondary,
        fontSize,
        border: `1px solid ${theme.palette.primary.main}`,
        paddingTop: 5,
        paddingBottom: 5
    },
    grouping: false,
    searchFieldVariant: 'outlined',
    exportButton: true,
    exportAllData: true,
    search: true,
    paging: rowCount > 10,
    exportFileName: moment(new Date()).format('YYYYMMDD.hhmmss'),
    pageSizeOptions: [10, 25, 50, 100],
    showTitle: false,
    toolbar: true,
    sort: true
});

const BING_ASSISTANT_VIEWS = ['Performance', 'Campaign', 'Search', 'Top Cities', 'Notes'];
const bingDescriptions = ['Follow search visibility, response, and attributed outcomes.', 'Compare campaign traffic, conversions, and attributed value.', 'Find the search terms earning clicks and meaningful actions.', 'Explore where search activity and conversions are concentrated.', 'Review context and annotations for this reporting period.'];

const Bing = (props) => {
    const { reportProfile, setLoading: setParentLoading } = props;
    const { appVars, appLoading } = useContext(AppVarsContext);
    const [value, setValue] = useState(0);
    const [topConversionData, setTopConversionData] = useState([]);
    const [keywordData, setKeywordData] = useState([]);
    const [regionData, setRegionData] = useState([]);
    const [campaignData, setCampaignData] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedMetricKey, setSelectedMetricKey] = useState('impressions');
    const admintoken = sessionStorage.getItem('_buta');

    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showCost = resolveShowCost(reportProfile);
    const bingAssistantConfig = useMemo(() => ({
        activeView: BING_ASSISTANT_VIEWS[value] || 'Campaign'
    }), [value]);

    const dateRangeLabel = (() => {
        const sDate = sessionStorage.getItem('sDate');
        const eDate = sessionStorage.getItem('eDate');
        if (!sDate || !eDate) return 'Selected report range';
        const formatDate = (value) => new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
        }).format(new Date(value));
        return `${formatDate(sDate)} - ${formatDate(eDate)}`;
    })();

    const pageShellSx = getReportPageSx(theme);
    const surfaceCardSx = getReportSurfaceSx(theme);
    const heroCardSx = surfaceCardSx;
    const surfacePanelSx = { ...surfaceCardSx, p: { xs: 1.5, md: 2.5 } };
    const metricTileSx = { ...surfaceCardSx, p: { xs: 1.5, md: 2 }, display: 'grid', gap: 0.75 };
    const tabsShellSx = { ...surfaceCardSx, px: { xs: 1, md: 1.5 }, py: 0.5 };

    const campaignStats = useMemo(() => (
        normalizePerformanceRows(campaignData, ['CAMPAIGN', 'Campaign', 'campaign', 'campaign_name', 'CAMPAIGN_NAME'])
    ), [campaignData]);

    const searchStats = useMemo(() => (
        normalizePerformanceRows(keywordData, ['SEARCH', 'TERM', 'KEYWORD', 'QUERY', 'SEARCH_TERM', 'search'])
    ), [keywordData]);

    const topConversionStats = useMemo(() => (
        normalizePerformanceRows(topConversionData, ['SEARCH', 'TERM', 'KEYWORD', 'QUERY', 'SEARCH_TERM', 'search'])
    ), [topConversionData]);

    const cityStats = useMemo(() => {
        return normalizeRegionRows(regionData)
            .map((row) => {
                const city = pick(row, ['CITY', 'City', 'city', 'METRO', 'Region']) || row?.CITY || 'Unknown';
                const impressions = safeNum(pick(row, ['IMPRESSIONS', 'Impressions', 'impressions', 'IMPR', 'impr']));
                const clicks = safeNum(pick(row, ['CLICKS', 'Clicks', 'clicks']));
                const conversions = safeNum(pick(row, ['CONVERSIONS', 'Conversions', 'conversions', 'CONV', 'conv']));
                const spend = safeNum(pick(row, ['COST', 'Cost', 'SPEND', 'Spend']));
                const revenue = safeNum(pick(row, ['REVENUE', 'Revenue']));
                const ctr = impressions ? (clicks / impressions) * 100 : 0;
                const convRate = clicks ? (conversions / clicks) * 100 : 0;
                return {
                    city,
                    impressions,
                    clicks,
                    conversions,
                    spend,
                    revenue,
                    ctr,
                    convRate
                };
            })
            .filter((row) => row.city && (row.impressions > 0 || row.clicks > 0 || row.conversions > 0));
    }, [regionData]);

    const metricOptions = useMemo(() => ([
        { key: 'impressions', label: 'Impressions', available: cityStats.some((row) => row.impressions > 0) },
        { key: 'clicks', label: 'Clicks', available: cityStats.some((row) => row.clicks > 0) },
        { key: 'conversions', label: 'Conversions', available: cityStats.some((row) => row.conversions > 0) }
    ]), [cityStats]);

    const fallbackMetricKey = useMemo(() => (
        metricOptions.find((option) => option.available)?.key || 'impressions'
    ), [metricOptions]);

    useEffect(() => {
        const current = metricOptions.find((option) => option.key === selectedMetricKey && option.available);
        if (!current) {
            setSelectedMetricKey(fallbackMetricKey);
        }
    }, [metricOptions, selectedMetricKey, fallbackMetricKey]);

    const coverageMetricKey = selectedMetricKey || fallbackMetricKey;
    const coverageMetricLabel = useMemo(() => {
        switch (coverageMetricKey) {
            case 'clicks':
                return 'Clicks';
            case 'conversions':
                return 'Conversions';
            default:
                return 'Impressions';
        }
    }, [coverageMetricKey]);

    const totalCoverage = useMemo(() => (
        cityStats.reduce((acc, row) => acc + safeNum(row[coverageMetricKey]), 0)
    ), [cityStats, coverageMetricKey]);

    const topCities = useMemo(() => (
        [...cityStats].sort((a, b) => safeNum(b[coverageMetricKey]) - safeNum(a[coverageMetricKey])).slice(0, 12)
    ), [cityStats, coverageMetricKey]);

    const topCity = useMemo(() => (topCities[0] || null), [topCities]);

    const topCampaigns = useMemo(() => (
        [...campaignStats].sort((a, b) => b.clicks - a.clicks).slice(0, 6)
    ), [campaignStats]);

    const topSearchTerms = useMemo(() => (
        [...searchStats].sort((a, b) => b.clicks - a.clicks).slice(0, 6)
    ), [searchStats]);

    const topConversionTerms = useMemo(() => (
        [...topConversionStats].sort((a, b) => b.conversions - a.conversions || b.clicks - a.clicks).slice(0, 6)
    ), [topConversionStats]);

    const topCampaignByCtr = useMemo(() => (
        [...campaignStats].filter((row) => row.clicks > 0).sort((a, b) => b.ctr - a.ctr)[0] || null
    ), [campaignStats]);

    const topSearchByConversions = useMemo(() => (
        [...topConversionStats].sort((a, b) => b.conversions - a.conversions || b.convRate - a.convRate)[0] || null
    ), [topConversionStats]);

    const campaignBarSeries = useMemo(() => ([
        { name: 'Clicks', data: topCampaigns.map((row) => row.clicks) },
        { name: 'Conversions', data: topCampaigns.map((row) => row.conversions) }
    ]), [topCampaigns]);

    const searchBarSeries = useMemo(() => ([
        { name: 'Clicks', data: topSearchTerms.map((row) => row.clicks) },
        { name: 'Conversions', data: topSearchTerms.map((row) => row.conversions) }
    ]), [topSearchTerms]);

    const conversionRateSeries = useMemo(() => ([
        { name: 'Conv Rate', data: topConversionTerms.map((row) => Number(row.convRate.toFixed(2))) }
    ]), [topConversionTerms]);

    const cityTreemapSeries = useMemo(() => ([
        {
            data: topCities.map((row) => ({
                x: row.city,
                y: safeNum(row[coverageMetricKey])
            }))
        }
    ]), [topCities, coverageMetricKey]);

    const cityBubbleSeries = useMemo(() => ([
        {
            name: 'Cities',
            data: topCities.map((row) => ({
                x: Number(row.ctr.toFixed(2)),
                y: Number(row.convRate.toFixed(2)),
                z: Math.max(1, safeNum(row[coverageMetricKey])),
                city: row.city,
                clicks: row.clicks,
                conversions: row.conversions
            }))
        }
    ]), [topCities, coverageMetricKey]);

    const barChartOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'bar', toolbar: { show: false }, zoom: { enabled: false } },
        plotOptions: {
            bar: {
                horizontal: true,
                barHeight: '58%',
                borderRadius: 4
            }
        },
        colors: [reportColors.revenue, reportColors.conversions],
        grid: {
            borderColor: alpha(theme.palette.primary.main, 0.12)
        },
        dataLabels: { enabled: false },
        legend: { position: 'top' },
        xaxis: {
            labels: { formatter: (val) => formatNumber(val) }
        },
        tooltip: {
            y: { formatter: (val) => formatNumber(val) }
        }
    }), [theme]);

    const percentBarOptions = useMemo(() => styleReportChart(theme, {
        ...barChartOptions,
        colors: [reportColors.conversions],
        tooltip: {
            y: { formatter: (val) => formatPercent(val) }
        },
        xaxis: {
            labels: { formatter: (val) => formatPercent(val) }
        }
    }), [barChartOptions, theme]);

    const treemapOptions = useMemo(() => styleReportChart(theme, {
        chart: { type: 'treemap', toolbar: { show: false } },
        plotOptions: {
            treemap: {
                distributed: true,
                enableShades: false
            }
        },
        colors: [reportColors.revenue],
        dataLabels: {
            enabled: true,
            style: { fontSize: '11px', fontWeight: 600 }
        },
        tooltip: {
            y: {
                formatter: (value) => formatNumber(value)
            }
        }
    }), [theme]);

    const bubbleOptions = useMemo(() => styleReportChart(theme, {
        colors: [reportColors.revenue],
        chart: { type: 'bubble', toolbar: { show: false } },
        grid: { borderColor: alpha(theme.palette.primary.main, 0.12) },
        xaxis: {
            title: { text: 'CTR (%)' },
            labels: { formatter: (val) => `${Number(val).toFixed(1)}%` }
        },
        yaxis: {
            title: { text: 'Conversion Rate (%)' },
            labels: { formatter: (val) => `${Number(val).toFixed(1)}%` }
        },
        dataLabels: { enabled: false },
        tooltip: {
            custom: ({ seriesIndex, dataPointIndex, w }) => {
                const point = w?.config?.series?.[seriesIndex]?.data?.[dataPointIndex];
                if (!point) return '';
                return `
                    <div style="padding:8px 10px;min-width:180px;">
                        <div style="font-weight:700;margin-bottom:4px;">${point.city}</div>
                        <div>CTR: ${formatPercent(point.x)}</div>
                        <div>Conv Rate: ${formatPercent(point.y)}</div>
                        <div>${coverageMetricLabel}: ${formatNumber(point.z)}</div>
                        <div>Clicks: ${formatNumber(point.clicks)}</div>
                        <div>Conversions: ${formatNumber(point.conversions)}</div>
                    </div>
                `;
            }
        }
    }), [theme, coverageMetricLabel]);

    const roiValue = useMemo(() => {
        const revenue = safeNum(data?.revenue?.count);
        const cost = safeNum(data?.cost?.count);
        return cost ? (revenue / cost) * 100 : 0;
    }, [data]);

    const fetchReportData = useCallback(async () => {
        if (!reportProfile || !appVars?.api_url) return;

        let settings = null;
        try {
            settings = typeof reportProfile.settings === 'object'
                ? reportProfile.settings
                : JSON.parse(reportProfile.settings);
        } catch (error) {
            console.error('Invalid Bing reportProfile.settings payload', error);
            return;
        }

        const aid = settings?.ad_sets?.bing?.id;
        if (!aid) return;

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                cid: sessionStorage.getItem('rid'),
                aid,
                sdate: sessionStorage.getItem('sDate'),
                edate: sessionStorage.getItem('eDate')
            }),
            redirect: 'follow'
        };

        setLoading(true);
        setParentLoading?.(true);

        try {
            const fetchJson = async (path) => {
                const response = await fetch(`${appVars.api_url}${path}`, requestOptions);
                if (!response.ok) {
                    throw new Error(`Bing request failed (${response.status})`);
                }
                return response.json();
            };

            const [dataResult, campaignResult, keywordResult, regionResult] = await Promise.allSettled([
                fetchJson('/bing/data'),
                fetchJson('/bing/campaigns'),
                fetchJson('/bing/keyword'),
                fetchJson('/bing/region')
            ]);

            if (dataResult.status === 'fulfilled') {
                const payload = dataResult.value;
                setData(Array.isArray(payload) ? payload[0] : payload?.[0] || payload);
            }
            if (campaignResult.status === 'fulfilled') {
                setCampaignData(normalizeCampaignRows(campaignResult.value || []));
            }
            if (keywordResult.status === 'fulfilled') {
                const keywordPayload = keywordResult.value || [];
                const searchSummary = Array.isArray(keywordPayload) ? (keywordPayload[0] || []) : [];
                const keywordSummary = Array.isArray(keywordPayload) ? (keywordPayload[1] || []) : [];
                setKeywordData(normalizeKeywordRows(searchSummary));
                setTopConversionData(
                    normalizeKeywordRows(keywordSummary).filter((item) => safeNum(item.CONVERSIONS || item.conversions) > 0)
                );
            }
            if (regionResult.status === 'fulfilled') {
                setRegionData(normalizeRegionRows(regionResult.value || []));
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
            setParentLoading?.(false);
        }
    }, [appVars?.api_url, reportProfile, setParentLoading]);

    useEffect(() => {
        if (!appVars || appLoading || !reportProfile) return;
        fetchReportData();
    }, [appVars, appLoading, reportProfile, fetchReportData]);

    useEffect(() => {
        const handleStorageChange = () => {
            fetchReportData();
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchReportData]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
    };

    if (!data && loading) {
        return (
            <ThemeProvider theme={theme}><Box sx={pageShellSx}>
                <Box sx={{ ...heroCardSx, p: { xs: 2, md: 2.5 } }}>
                    <Stack spacing={2}>
                        <Skeleton variant="text" width={180} height={28} />
                        <Skeleton variant="text" width={320} height={42} />
                        <Skeleton variant="text" width={220} height={24} />
                        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
                            {Array.from({ length: 8 }).map((_, index) => (
                                <Skeleton key={`bing-kpi-skel-${index}`} variant="rectangular" height={110} sx={{ borderRadius: 3 }} />
                            ))}
                        </Box>
                    </Stack>
                </Box>
            </Box></ThemeProvider>
        );
    }

    if (!data && !loading) {
        return (
            <ThemeProvider theme={theme}><Box sx={pageShellSx}>
                <Box sx={{ ...surfacePanelSx, textAlign: 'center', py: 5 }}>
                    <Typography variant="h6" gutterBottom>No Bing data available</Typography>
                    <Typography color="text.secondary">
                        No Bing data is available for the selected date range.
                    </Typography>
                </Box>
            </Box></ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}><Box sx={pageShellSx}>
            <Stack spacing={2} sx={{ position: 'relative' }}>

                {loading ? <LinearProgress /> : null}

                <Box sx={tabsShellSx}>
                    <ResponsiveTabs
                        value={value}
                        indicatorColor="primary"
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Bing report tabs"
                        sx={getAnalyticsReportTabsSx()}
                    >
                        <Tab label="Performance" {...a11yProps(0)} />
                        <Tab label="Campaign" {...a11yProps(1)} />
                        <Tab label="Search" {...a11yProps(2)} />
                        <Tab label="Top Cities" {...a11yProps(3)} />
                        <Tab label="Notes" {...a11yProps(4)} />
                    </ResponsiveTabs>
                </Box>

                {value > 0 ? <ReportSectionIntro report="Microsoft Ads" title={`Bing · ${BING_ASSISTANT_VIEWS[value]}`} description={bingDescriptions[value]} dateLabel={dateRangeLabel} /> : null}
                <TabPanel value={value} index={0} data-title="Performance">
                    <ReportPerformanceOverview eyebrow="Microsoft Ads" title="Bing Search Performance"
                        description={`Understand how search visibility turns into traffic and attributed actions during ${dateRangeLabel}.`}
                        badge={`${campaignStats.length} campaigns`}
                        stages={[
                            { label: 'Acquisition', description: 'Search visibility and traffic generated', metrics: [
                                { label: 'Impressions', value: formatNumber(data?.impressions?.count), detail: 'Delivered impressions' },
                                { label: 'Clicks', value: formatNumber(data?.clicks?.count), detail: 'Traffic generated' }
                            ] },
                            { label: 'Response', description: 'How effectively search traffic responds', metrics: [
                                { label: 'CTR', value: formatPercent(data?.ctr?.count), detail: 'Click-through rate' },
                                { label: 'Conversion rate', value: formatPercent(data?.convrate?.count), detail: 'Conversions relative to clicks' }
                            ] },
                            { label: 'Outcome', description: 'Attributed actions and their value', metrics: [
                                { label: 'Conversions', value: formatNumber(data?.conversions?.count), detail: 'Recorded conversions' },
                                { label: 'Revenue', value: formatCurrency(data?.revenue?.count), detail: 'Attributed revenue' }
                            ] }
                        ]}
                        supportMetrics={[{ label: 'Average transaction', value: formatCurrency(data?.avgGift?.count) }, { label: 'Search terms', value: formatNumber(searchStats.length) }, { label: 'Cities', value: formatNumber(cityStats.length) }, ...(showCost ? [{ label: 'Spend', value: formatCurrency(data?.cost?.count) }, { label: 'CPC', value: formatCurrency(data?.cpc?.count) }] : [])]}
                        insight={topCampaigns[0] ? `${topCampaigns[0].label} generated the most campaign traffic with ${formatNumber(topCampaigns[0].clicks)} clicks and ${formatNumber(topCampaigns[0].conversions)} conversions.` : 'No campaign activity was returned for this range.'}
                        highlights={[
                            { label: 'Top campaign by clicks', title: topCampaigns[0]?.label || 'No campaign data', value: formatNumber(topCampaigns[0]?.clicks), detail: 'Clicks from the leading campaign.' },
                            { label: 'Leading converting search', title: topSearchByConversions?.label || 'No search data', value: formatNumber(topSearchByConversions?.conversions), detail: 'Conversions from the leading search term.' },
                            { label: 'Top city', title: topCity?.city || 'No city data', value: formatNumber(topCity?.[coverageMetricKey]), detail: `Ranked by ${coverageMetricLabel.toLowerCase()}.` }
                        ]} loading={loading && !data}
                    />
                    <Box sx={{ ...surfacePanelSx, mt: 2 }}>
                        <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700 }}>Traffic and actions by campaign</Typography>
                        <Typography variant="body2" color="text.secondary">Compare clicks with conversions; open Campaign for exact detail.</Typography>
                        <ReactApexChart options={{ ...barChartOptions, xaxis: { ...barChartOptions.xaxis, categories: topCampaigns.map((row) => row.label) } }} series={campaignBarSeries} type="bar" height={320} />
                    </Box>
                </TabPanel>

                <TabPanel value={value} index={1} data-title="Campaign">
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 6.5fr) minmax(0, 5.5fr)' }
                            }}
                        >
                            <Box sx={surfacePanelSx}>
                                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                    Top Campaign Drivers
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Click and conversion leaders across the highest-performing campaigns.
                                </Typography>
                                {topCampaigns.length ? (
                                    <ReactApexChart
                                        options={{
                                            ...barChartOptions,
                                            xaxis: {
                                                ...barChartOptions.xaxis,
                                                categories: topCampaigns.map((row) => row.label)
                                            }
                                        }}
                                        series={campaignBarSeries}
                                        type="bar"
                                        height={isMobile ? 280 : 340}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No campaign performance data available.
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={surfacePanelSx}>
                                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                    Campaign Snapshot
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Fast readout of the strongest campaign signals in this range.
                                </Typography>
                                <Stack spacing={1.25}>
                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography variant="caption" color="text.secondary">Most Clicks</Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {topCampaigns[0]?.label || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {topCampaigns[0] ? `${formatNumber(topCampaigns[0].clicks)} clicks • ${formatPercent(topCampaigns[0].ctr)} CTR` : 'No click data available.'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography variant="caption" color="text.secondary">Best CTR</Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {topCampaignByCtr?.label || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {topCampaignByCtr ? `${formatPercent(topCampaignByCtr.ctr)} CTR on ${formatNumber(topCampaignByCtr.impressions)} impressions` : 'No CTR leader available.'}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography variant="caption" color="text.secondary">Revenue / Efficiency</Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {showCost ? formatPercent(roiValue) : formatCurrency(data?.revenue?.count)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {showCost
                                                ? `${formatCurrency(data?.cost?.count)} spend returned ${formatCurrency(data?.revenue?.count)} in revenue.`
                                                : `${formatCurrency(data?.revenue?.count)} attributed revenue in the selected range.`}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        </Box>

                        <Box sx={surfacePanelSx}>
                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                Campaign Table
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Full campaign-level performance with exports and search.
                            </Typography>
                            <TableCard
                                branded={false}
                                title="Campaigns"
                                data={campaignData}
                                count={(campaignData.length > 5 ? 5 : campaignData.length)}
                                options={buildTableOptions(theme, campaignData.length)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}
                                reportProfile={reportProfile}
                                columns={[
                                    {
                                        header: 'Campaigns',
                                        field: 'name',
                                        grow: true,
                                        minSize: 400,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={13} fontWeight={900}>
                                                {cell.row.original.CAMPAIGN}
                                            </Typography>
                                        ),
                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign="right">Totals</Typography>,
                                    }
                                ]}
                            />
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={value} index={2} data-title="Search">
                    <Stack spacing={2}>
                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 6.5fr) minmax(0, 5.5fr)' }
                            }}
                        >
                            <Box sx={surfacePanelSx}>
                                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                    Search Term Momentum
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Which queries drove the most traffic and conversion volume.
                                </Typography>
                                {topSearchTerms.length ? (
                                    <ReactApexChart
                                        options={{
                                            ...barChartOptions,
                                            xaxis: {
                                                ...barChartOptions.xaxis,
                                                categories: topSearchTerms.map((row) => row.label)
                                            }
                                        }}
                                        series={searchBarSeries}
                                        type="bar"
                                        height={isMobile ? 280 : 340}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No search term data available.
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={surfacePanelSx}>
                                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                    Conversion Efficiency
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Terms with the strongest conversion rate in the current range.
                                </Typography>
                                {topConversionTerms.length ? (
                                    <ReactApexChart
                                        options={{
                                            ...percentBarOptions,
                                            xaxis: {
                                                ...percentBarOptions.xaxis,
                                                categories: topConversionTerms.map((row) => row.label)
                                            }
                                        }}
                                        series={conversionRateSeries}
                                        type="bar"
                                        height={isMobile ? 280 : 340}
                                    />
                                ) : (
                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography variant="caption" color="text.secondary">Top Conversion Term</Typography>
                                        <Typography sx={{ fontWeight: 700 }}>
                                            {topSearchByConversions?.label || '—'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {topSearchByConversions
                                                ? `${formatNumber(topSearchByConversions.conversions)} conversions • ${formatPercent(topSearchByConversions.convRate)} conversion rate`
                                                : 'No conversion keyword data available.'}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>

                        <Box sx={surfacePanelSx}>
                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                Search Terms Table
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Full search query performance with search and export controls.
                            </Typography>
                            <TableCard
                                branded={false}
                                title="Search"
                                data={keywordData}
                                count={5}
                                options={buildTableOptions(theme, keywordData.length)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}
                                reportProfile={reportProfile}
                                columns={[
                                    {
                                        header: 'Search Terms',
                                        field: 'name',
                                        grow: true,
                                        minSize: 400,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={13} fontWeight={900}>
                                                {cell.row.original.SEARCH}
                                            </Typography>
                                        ),
                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign="right">Totals</Typography>,
                                    }
                                ]}
                            />
                        </Box>

                        <Box sx={surfacePanelSx}>
                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                Top Conversion Terms
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Search terms already filtered down to entries with conversion activity.
                            </Typography>
                            <TableCard
                                branded={false}
                                title="KEYWORD"
                                data={topConversionData}
                                count={(topConversionData.length > 5 ? 5 : topConversionData.length)}
                                options={buildTableOptions(theme, topConversionData.length)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}
                                reportProfile={reportProfile}
                                columns={[
                                    {
                                        header: 'Keywords',
                                        field: 'name',
                                        grow: true,
                                        minSize: 400,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={13} fontWeight={900}>
                                                {cell.row.original.SEARCH}
                                            </Typography>
                                        ),
                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign="right">Totals</Typography>,
                                    }
                                ]}
                            />
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={value} index={3} data-title="Cities">
                    <Stack spacing={2}>
                        <Box sx={surfacePanelSx}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
                                {metricOptions.some((option) => option.available) ? (
                                    <FormControl size="small" sx={{ minWidth: 180 }}>
                                        <InputLabel id="bing-city-metric-label">Metric</InputLabel>
                                        <Select
                                            labelId="bing-city-metric-label"
                                            value={coverageMetricKey}
                                            label="Metric"
                                            onChange={(event) => setSelectedMetricKey(event.target.value)}
                                        >
                                            {metricOptions.filter((option) => option.available).map((option) => (
                                                <MenuItem key={option.key} value={option.key}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                ) : null}
                                <Chip
                                    size="small"
                                    label={`Total ${coverageMetricLabel}: ${formatNumber(totalCoverage)}`}
                                    sx={{ backgroundColor: theme.palette.divider }}
                                />
                                {topCity ? (
                                    <Chip
                                        size="small"
                                        label={`Top City: ${topCity.city} (${formatNumber(topCity[coverageMetricKey])})`}
                                        sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.16) }}
                                    />
                                ) : null}
                                <Chip
                                    size="small"
                                    label={`Cities with activity: ${formatNumber(cityStats.length)}`}
                                    sx={{ backgroundColor: alpha(theme.palette.info.main, 0.16) }}
                                />
                            </Box>

                            {cityStats.length ? (
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 2,
                                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 6fr) minmax(0, 6fr)' }
                                    }}
                                >
                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                            City Coverage Map
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            Relative share by {coverageMetricLabel.toLowerCase()}.
                                        </Typography>
                                        <ReactApexChart
                                            options={treemapOptions}
                                            series={cityTreemapSeries}
                                            type="treemap"
                                            height={isMobile ? 260 : 320}
                                        />
                                    </Box>

                                    <Box sx={{ ...metricTileSx, p: 1.25 }}>
                                        <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                            Performance Matrix
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            CTR vs conversion rate with bubble size mapped to coverage.
                                        </Typography>
                                        <ReactApexChart
                                            options={bubbleOptions}
                                            series={cityBubbleSeries}
                                            type="bubble"
                                            height={isMobile ? 260 : 320}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    No city data available for the selected range.
                                </Typography>
                            )}
                        </Box>

                        {cityStats.length ? (
                            <Box sx={surfacePanelSx}>
                                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                    Top City Coverage
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    The cities currently carrying the most {coverageMetricLabel.toLowerCase()}.
                                </Typography>
                                <Stack spacing={1}>
                                    {topCities.slice(0, 8).map((row) => {
                                        const metricValue = safeNum(row[coverageMetricKey]);
                                        const percent = totalCoverage ? (metricValue / totalCoverage) * 100 : 0;
                                        return (
                                            <Box key={`city-bar-${row.city}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ minWidth: 120 }}>
                                                    <Typography variant="body2" fontWeight={700}>
                                                        {row.city}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatNumber(metricValue)} {coverageMetricLabel.toLowerCase()}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <LinearProgress
                                                        variant="determinate"
                                                        value={Math.min(100, percent)}
                                                        sx={{
                                                            height: 9,
                                                            borderRadius: 999,
                                                            backgroundColor: theme.palette.divider,
                                                            '& .MuiLinearProgress-bar': { bgcolor: reportColors.revenue }
                                                        }}
                                                    />
                                                </Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatPercent(percent)}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : null}

                        <Box sx={surfacePanelSx}>
                            <Typography component="h2" variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', mb: 0.5 }}>
                                City Table
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Full metro/city coverage detail for export and deeper review.
                            </Typography>
                            <TableCard
                                branded={false}
                                title="Top Cities"
                                data={regionData}
                                count={(regionData.length > 10 ? 10 : regionData.length)}
                                options={buildTableOptions(theme, regionData.length)}
                                themeColor={{
                                    primary: theme.palette.primary.main,
                                    secondary: theme.palette.primary.main
                                }}
                                reportProfile={reportProfile}
                                columns={[
                                    {
                                        header: 'City',
                                        field: 'CITY',
                                        grow: true,
                                        minSize: 200,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={13} fontWeight={900}>
                                                {cell.row.original.CITY}
                                            </Typography>
                                        ),
                                        Footer: () => <Typography fontSize={13} fontWeight={900} textAlign="right">Totals</Typography>,
                                    },
                                    {
                                        header: 'Impr',
                                        field: 'IMPRESSIONS',
                                        minSize: 90,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={12}>
                                                {formatNumber(pick(cell.row.original, ['IMPRESSIONS', 'Impressions', 'impressions', 'IMPR']) || 0)}
                                            </Typography>
                                        )
                                    },
                                    {
                                        header: 'Clicks',
                                        field: 'CLICKS',
                                        minSize: 90,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={12}>
                                                {formatNumber(pick(cell.row.original, ['CLICKS', 'Clicks', 'clicks']) || 0)}
                                            </Typography>
                                        )
                                    },
                                    {
                                        header: 'Conv',
                                        field: 'CONVERSIONS',
                                        minSize: 90,
                                        Cell: ({ cell }) => (
                                            <Typography textAlign="right" fontSize={12}>
                                                {formatNumber(pick(cell.row.original, ['CONVERSIONS', 'Conversions', 'conversions', 'CONV', 'conv']) || 0)}
                                            </Typography>
                                        )
                                    },
                                    {
                                        header: 'CTR',
                                        field: 'CTR',
                                        minSize: 90,
                                        Cell: ({ cell }) => {
                                            const impressions = safeNum(pick(cell.row.original, ['IMPRESSIONS', 'Impressions', 'impressions', 'IMPR']));
                                            const clicks = safeNum(pick(cell.row.original, ['CLICKS', 'Clicks', 'clicks']));
                                            return (
                                                <Typography textAlign="right" fontSize={12}>
                                                    {formatPercent(impressions ? (clicks / impressions) * 100 : 0)}
                                                </Typography>
                                            );
                                        }
                                    },
                                    {
                                        header: 'Conv Rate',
                                        field: 'CONV_RATE',
                                        minSize: 110,
                                        Cell: ({ cell }) => {
                                            const clicks = safeNum(pick(cell.row.original, ['CLICKS', 'Clicks', 'clicks']));
                                            const conversions = safeNum(pick(cell.row.original, ['CONVERSIONS', 'Conversions', 'conversions', 'CONV', 'conv']));
                                            return (
                                                <Typography textAlign="right" fontSize={12}>
                                                    {formatPercent(clicks ? (conversions / clicks) * 100 : 0)}
                                                </Typography>
                                            );
                                        }
                                    }
                                ]}
                            />
                        </Box>
                    </Stack>
                </TabPanel>

                <TabPanel value={value} index={4} data-title="Notes">
                    <Box sx={surfacePanelSx}>
                        <Comments
                            sDate={sessionStorage.getItem('sDate')}
                            eDate={sessionStorage.getItem('eDate')}
                            color={theme.palette.primary.main}
                            admintoken={admintoken}
                            view="a7edd5c0-7a77-49ed-86ad-6fe3066a0f59"
                            type="bing"
                            setLoading={setParentLoading}
                        />
                    </Box>
                </TabPanel>
            </Stack>
            <ReportQuestionAssistant
                reportType="bing"
                reportTitle="Bing"
                profile={reportProfile}
                contextPayload={bingAssistantConfig}
            />
        </Box></ThemeProvider>
    );
};

export default Bing;
